import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { addStore } from "../../../lib/webService/webService";
import { useUserContext } from "../../../context/UserContext";
import { ArrowLeft } from "react-bootstrap-icons";
import { toast } from "react-toastify";
import { Seo } from "../../../components/Seo";
import { generateNoIndexSEO } from "../../../lib/seo/metaGenerators";
import { useFormDraft } from "../../../hooks/useFormDraft";

// ─── Module-level CSS ────────────────────────────────────────────────────────
if (typeof document !== "undefined" && !document.getElementById("as-styles")) {
  const s = document.createElement("style");
  s.id = "as-styles";
  s.textContent = `
    @keyframes _as_sk { 0%,100%{opacity:1}50%{opacity:.4} }
    ._as_sk { animation: _as_sk 1.8s ease-in-out infinite; background: rgba(60,60,67,0.08); }
    .as-input { background: rgba(118,118,128,0.10) !important; border: 1px solid transparent !important; border-radius: 10px !important; padding: 11px 14px !important; font-size: 0.9375rem !important; color: #1c1c1e !important; transition: border-color .18s, box-shadow .18s !important; width: 100%; outline: none; font-family: inherit; box-sizing: border-box; appearance: none; }
    .as-input:focus { border-color: #007AFF !important; box-shadow: 0 0 0 3px rgba(0,122,255,0.12) !important; background: rgba(118,118,128,0.08) !important; }
    .as-input::placeholder { color: rgba(60,60,67,0.32) !important; }
    .as-input.as-invalid { border-color: #FF3B30 !important; box-shadow: 0 0 0 3px rgba(255,59,48,0.10) !important; }
    .as-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
    .as-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; }
    @media (max-width: 480px) {
      .as-grid-2, .as-grid-3 { grid-template-columns: 1fr; }
    }
  `;
  document.head.appendChild(s);
}

const FONT =
  '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif';
const PAGE = { minHeight: "100vh", backgroundColor: "#F2F2F7", fontFamily: FONT };

const FieldLabel = ({ children, htmlFor }) => (
  <label
    htmlFor={htmlFor}
    style={{
      fontSize: "0.72rem",
      fontWeight: 600,
      color: "rgba(60,60,67,0.55)",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      display: "block",
      marginBottom: 7,
    }}
  >
    {children}
  </label>
);

const FieldError = ({ children }) => (
  <div style={{ fontSize: "0.75rem", color: "#FF3B30", marginTop: 4 }}>{children}</div>
);

// Custom hook for form state management
const useFormState = (initialState = {}) => {
  const [formState, setFormState] = useState({
    validated: false,
    loading: false,
    error: null,
    ...initialState,
  });

  const setFormLoading = useCallback((loading) => {
    setFormState((prev) => ({ ...prev, loading, error: null }));
  }, []);

  const setFormError = useCallback((error) => {
    setFormState((prev) => ({ ...prev, loading: false, error }));
  }, []);

  const setValidated = useCallback((validated) => {
    setFormState((prev) => ({ ...prev, validated }));
  }, []);

  return { formState, setFormLoading, setFormError, setValidated };
};

const AddStoreForm = () => {
  const navigate = useNavigate();
  const { token } = useUserContext();
  const { formState, setFormLoading, setFormError, setValidated } =
    useFormState();

  const [formData, setFormData] = useState({
    name: "",
    account: "",
    account_ref: "",
    account_type: "",
    category: "",
    imgSrc: "",
    bannerImgSrc: "",
    location: {
      latitude: "",
      longitude: "",
    },
  });

  const { restoredDraft, clearDraft } = useFormDraft("add-store", formData);

  useEffect(() => {
    if (restoredDraft) setFormData(restoredDraft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = useCallback(
    (field) => (e) => {
      const value = e.target.value;
      if (field === "latitude" || field === "longitude") {
        setFormData((prev) => ({
          ...prev,
          location: {
            ...prev.location,
            [field]: value,
          },
        }));
      } else {
        setFormData((prev) => ({ ...prev, [field]: value }));
      }
    },
    []
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setValidated(true);

    // Validate required fields
    if (
      !formData.name.trim() ||
      !formData.account.trim() ||
      !formData.account_type.trim() ||
      !formData.category.trim() ||
      !formData.imgSrc.trim() ||
      !formData.location.latitude.trim() ||
      !formData.location.longitude.trim()
    ) {
      setFormError({ message: "Please fill in all required fields" });
      return;
    }

    try {
      // Format the data before sending to API
      const formattedData = {
        store_name: formData.name,
        imgSrc: formData.imgSrc,
        bannerImgSrc: formData.bannerImgSrc,
        location: {
          latitude: parseFloat(formData.location.latitude),
          longitude: parseFloat(formData.location.longitude),
        },
        store_category: formData.category,
        account_number: formData.account,
        account_reference: formData.account_ref,
        account_type: formData.account_type,
      };

      await addStore(formattedData, token);
      toast.success("Store added successfully!");
      clearDraft();
      navigate("/sup/stores");
    } catch (error) {
      setFormError({
        message: error.message || "Failed to add store. Please try again.",
      });
    } finally {
      setFormLoading(false);
    }
  };

  const isInvalid = (field) => {
    if (field === "latitude" || field === "longitude") {
      return formState.validated && !formData.location[field].trim();
    }
    return formState.validated && !formData[field]?.toString().trim();
  };

  return (
    <div style={PAGE}>
      <Seo {...generateNoIndexSEO("Add Store")} />
      <div style={{ maxWidth: 680, margin: "0 auto", padding: "28px 16px 48px" }}>

        {/* Back nav */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              background: "rgba(60,60,67,.08)",
              border: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: "#1c1c1e",
              flexShrink: 0,
            }}
          >
            <ArrowLeft size={16} />
          </button>
          <h2 style={{ fontSize: "1.375rem", fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.03em", margin: 0 }}>
            Add Store
          </h2>
        </div>

        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: 16,
            border: "1px solid rgba(60,60,67,.09)",
            padding: "20px 24px",
            boxShadow: "0 1px 3px rgba(0,0,0,.04)",
          }}
        >
          <form noValidate onSubmit={handleSubmit}>
            {formState.error && (
              <div
                style={{
                  backgroundColor: "rgba(255,59,48,.08)",
                  borderRadius: 10,
                  padding: "11px 14px",
                  marginBottom: 20,
                  fontSize: "0.875rem",
                  color: "#FF3B30",
                }}
              >
                {formState.error.message}
              </div>
            )}

            {/* Name + Category */}
            <div className="as-grid-2" style={{ marginBottom: 16 }}>
              <div>
                <FieldLabel htmlFor="as-name">Store Name</FieldLabel>
                <input
                  id="as-name"
                  type="text"
                  className={`as-input${isInvalid("name") ? " as-invalid" : ""}`}
                  value={formData.name}
                  onChange={handleInputChange("name")}
                  placeholder="Enter store name"
                  required
                />
                {isInvalid("name") && <FieldError>Please provide a store name</FieldError>}
              </div>
              <div>
                <FieldLabel htmlFor="as-category">Category</FieldLabel>
                <select
                  id="as-category"
                  className={`as-input${isInvalid("category") ? " as-invalid" : ""}`}
                  value={formData.category}
                  onChange={handleInputChange("category")}
                  required
                >
                  <option value="">Select category</option>
                  <option value="fast-food">Fast Food</option>
                  <option value="liquor">Liquor</option>
                </select>
                {isInvalid("category") && <FieldError>Please select a category</FieldError>}
              </div>
            </div>

            {/* Account / Account ref / Account type */}
            <div className="as-grid-3" style={{ marginBottom: 16 }}>
              <div>
                <FieldLabel htmlFor="as-account">Account</FieldLabel>
                <input
                  id="as-account"
                  type="text"
                  className={`as-input${isInvalid("account") ? " as-invalid" : ""}`}
                  value={formData.account}
                  onChange={handleInputChange("account")}
                  placeholder="Account"
                  required
                />
                {isInvalid("account") && <FieldError>Required</FieldError>}
              </div>
              <div>
                <FieldLabel htmlFor="as-account-ref">Account Ref (Optional)</FieldLabel>
                <input
                  id="as-account-ref"
                  type="text"
                  className="as-input"
                  value={formData.account_ref}
                  onChange={handleInputChange("account_ref")}
                  placeholder="Reference"
                />
              </div>
              <div>
                <FieldLabel htmlFor="as-account-type">Account Type</FieldLabel>
                <select
                  id="as-account-type"
                  type="dropdown"
                  className={`as-input${isInvalid("account_type") ? " as-invalid" : ""}`}
                  value={formData.account_type}
                  onChange={handleInputChange("account_type")}
                  placeholder="Type"
                  required
                >
                  <option value="">Select account type</option>
                  <option value="Paybill">Paybill</option>
                  <option value="TillNumber">Till Number</option>
                </select>
                {isInvalid("account_type") && <FieldError>Required</FieldError>}
              </div>
            </div>

            {/* Image URL */}
            <div style={{ marginBottom: 16 }}>
              <FieldLabel htmlFor="as-image">Image Source</FieldLabel>
              <input
                id="as-image"
                type="text"
                className={`as-input${isInvalid("imgSrc") ? " as-invalid" : ""}`}
                value={formData.imgSrc}
                onChange={handleInputChange("imgSrc")}
                placeholder="https://..."
                required
              />
              {isInvalid("imgSrc") && <FieldError>Please provide an image source</FieldError>}
            </div>

            {/* Banner Image URL */}
            <div style={{ marginBottom: 16 }}>
              <FieldLabel htmlFor="as-banner-image">Banner Image Source (optional)</FieldLabel>
              <input
                id="as-banner-image"
                type="text"
                className="as-input"
                value={formData.bannerImgSrc}
                onChange={handleInputChange("bannerImgSrc")}
                placeholder="https://... (falls back to Image Source if left blank)"
              />
            </div>

            {/* Latitude / Longitude */}
            <div className="as-grid-2" style={{ marginBottom: 24 }}>
              <div>
                <FieldLabel htmlFor="as-lat">Latitude</FieldLabel>
                <input
                  id="as-lat"
                  type="number"
                  step="any"
                  className={`as-input${isInvalid("latitude") ? " as-invalid" : ""}`}
                  value={formData.location.latitude}
                  onChange={handleInputChange("latitude")}
                  placeholder="Enter latitude"
                  required
                />
                {isInvalid("latitude") && <FieldError>Please provide a latitude</FieldError>}
              </div>
              <div>
                <FieldLabel htmlFor="as-lng">Longitude</FieldLabel>
                <input
                  id="as-lng"
                  type="number"
                  step="any"
                  className={`as-input${isInvalid("longitude") ? " as-invalid" : ""}`}
                  value={formData.location.longitude}
                  onChange={handleInputChange("longitude")}
                  placeholder="Enter longitude"
                  required
                />
                {isInvalid("longitude") && <FieldError>Please provide a longitude</FieldError>}
              </div>
            </div>

            <button
              type="submit"
              disabled={formState.loading}
              style={{
                width: "100%",
                padding: "13px",
                borderRadius: 12,
                border: "none",
                backgroundColor: formState.loading ? "rgba(0,122,255,.5)" : "#007AFF",
                color: "#fff",
                fontSize: "0.9375rem",
                fontWeight: 600,
                cursor: formState.loading ? "not-allowed" : "pointer",
                fontFamily: FONT,
                transition: "background-color .18s",
              }}
            >
              {formState.loading ? "Adding Store…" : "Add Store"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddStoreForm;
